import { Badge, Box, Button, DrawerBody, DrawerFooter, FormControl, FormHelperText, FormLabel, Input, Select, Stack, useToast } from "@chakra-ui/react";
import { DrawerContext } from "components/drawer";
import { AuthContext } from "contexts/AuthContext";
import { useLocation } from "hooks/useLocation";
import { getAll, save, updateMany } from "lib/firebase";
import { planBroadcastUpdates } from "lib/broadcast";
import { useContext, useEffect, useState } from "react";

export default function ContentModal({ content, onChange }) {
  const [data, setData] = useState(content || {})
  const { user } = useContext(AuthContext)

  const { closeDrawer } = useContext(DrawerContext)

  const toast = useToast()

  // Only another content item replaces the form. The content list follows the database live, so
  // the same item arrives again as a new object when anybody uploads: that must not wipe what is typed.
  useEffect(() => {
    if (content) {
      setData(content || {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content?.id])

  const updateContent = async () => {
    try {
      if (data.type === 'image' && !(data.duration >= 1)) {
        throw new Error('Duration must be at least 1 second.')
      }

      if (data.duration !== content.duration) {
        // The content is played inside broadcasts, so their total duration (and that
        // of the multiplier copies and composites built on them) changes with it.
        // Everything is written in ONE atomic update.
        const [broadcasts, contentItems] = await Promise.all([
          getAll('broadcast', content.clientId),
          getAll('content', content.clientId),
        ])
        const plan = planBroadcastUpdates({ broadcasts, contentItems, changedContent: { id: content.id, duration: data.duration } })
        await updateMany({ ...plan.updates, [`content/${content.id}`]: data })
      } else {
        await save('content', content.id, data)
      }
      onChange && onChange()
      closeDrawer()
      toast({ title: 'Content updated', status: 'success', duration: null, isClosable: true })
    } catch (err) {
      toast({ title: 'Error!', description: err.message, status: 'error', duration: null, isClosable: true })
    }
  }

  return (
    <>
      <DrawerBody>
        <Stack direction='column' spacing={4}>
          <FormControl>
            <FormLabel>ID</FormLabel>
            <Input type='text' value={data.id || 'ID will be auto-generated'} disabled />
          </FormControl>
          <FormControl>
            <FormLabel>Display name</FormLabel>
            <Input type='text' value={data.displayName || data.filename} onChange={e => setData({...data, displayName: e.target.value })} />
          </FormControl>
          {data.type === 'image' && <FormControl>
            <FormLabel>Duration</FormLabel>
            <Input type='number' min={1} step={1} value={data.duration ?? ''} onChange={e => {
              const seconds = parseInt(e.target.value, 10)
              setData({...data, duration: Number.isNaN(seconds) ? undefined : seconds })
            }} />
          </FormControl>}
        </Stack>
      </DrawerBody>
      <DrawerFooter>
        {content && (
          <Button colorScheme='brand' borderRadius="30px" me="10px" onClick={() => updateContent()}>
            Save
          </Button>
        )}
      </DrawerFooter>
    </>
  );
}